/* eslint-disable */

"use client"

import React from "react";
import {
  DeepPartial,
  getOverrideFromOverridesList,
  merge,
  ObjectValue,
  Value,
} from "@hypertune/sdk";
import toStartCase from "@hypertune/sdk/src/shared/helpers/toStartCase";
import {
  flagFallbacks,
  FlagPaths,
  FlagValues,
  RootNode,
  Source,
} from "./hypertune";
import { useHypertune, useSetOverride } from "./hypertune.react";
import "./hypertuneToolbar.css";

const buttonStyleLocalStorageKey = "hypertuneToolbarButtonStyle";
const rootFieldName = "root";
const rootPrefix = `${rootFieldName}.`;

type StyleProps = {
  modalBackgroundClassName?: string;
  modalClassName?: string;
  headerClassName?: string;
  mainClassName?: string;
  flagClassName?: string;
  flagNameContainerClassName?: string;
  flagIconContainerClassName?: string;
  flagNameClassName?: string;
  flagValueContainerClassName?: string;
  footerClassName?: string;
  searchTextInputClassName?: string;
  numberInputClassName?: string;
  toggleClassName?: string;
};

type PersistedStyle = {
  position: "fixed";
  top: number;
  left: number;
};

export default function HypertuneToolbar({
  showFlagPath,
  ...styleProps
}: StyleProps & {
  showFlagPath?: FlagPaths;
}): React.ReactElement | null {
  const hypertune = useHypertune();

  if (
    showFlagPath &&
    !hypertune.getFlagValues({ flagPaths: [showFlagPath], flagFallbacks })[
      showFlagPath
    ]
  ) {
    return null;
  }

  return <HypertuneToolbarInner {...styleProps} />;
}

function HypertuneToolbarInner(
  styleProps: StyleProps
): React.ReactElement | null {
  const [showControl, setShowControl] = React.useState(false);

  const ref = React.useRef<HTMLButtonElement>(null);
  const [dragOffset, setDragOffset] = React.useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [style, setStyle] = React.useState<PersistedStyle | undefined>(
    typeof window !== "undefined"
      ? (JSON.parse(
          window.localStorage.getItem(buttonStyleLocalStorageKey) ?? "null"
        ) ?? undefined)
      : undefined
  );

  const persistStyle = React.useCallback((newStyle: PersistedStyle) => {
    window.localStorage.setItem(
      buttonStyleLocalStorageKey,
      JSON.stringify(newStyle)
    );
  }, []);

  React.useLayoutEffect(() => {
    if (!style) {
      return;
    }
    function updateStyle() {
      if (!style) {
        return;
      }
      if (
        style.left >= 0 &&
        window.innerWidth - 124 >= style.left &&
        style.top >= 0 &&
        window.innerHeight - 42 >= style.top
      ) {
        return;
      }
      const newStyle: PersistedStyle = {
        position: "fixed",
        left:
          style.left < 0
            ? Math.min(4, window.innerWidth)
            : window.innerWidth - 124 < style.left
              ? Math.max(0, window.innerWidth - 124)
              : style.left,
        top:
          style.top < 0
            ? Math.min(4, window.innerHeight)
            : window.innerHeight - 42 < style.top
              ? Math.max(0, window.innerHeight - 42)
              : style.top,
      };
      setStyle(newStyle);
      persistStyle(newStyle);
    }
    window.addEventListener("resize", updateStyle);
    updateStyle();
    return () => window.removeEventListener("resize", updateStyle);
  }, [setStyle, persistStyle, style]);

  return (
    <>
      <div className="hypertuneToolbarMainButtonBackground">
        <button
          ref={ref}
          type="button"
          onClick={() => setShowControl(true)}
          className="hypertuneToolbarMainButton"
          draggable
          style={style}
          onDragStart={(event) => {
            const clientRect = ref.current?.getClientRects()?.[0];
            setDragOffset({
              x: event.clientX - (clientRect?.left ?? 0),
              y: event.clientY - (clientRect?.top ?? 0),
            });
          }}
          onDrag={(event) => {
            event.preventDefault();
            setStyle({
              position: "fixed",
              top: event.clientY - dragOffset.y,
              left: event.clientX - dragOffset.x,
            });
          }}
          onDragEnd={(event) => {
            event.preventDefault();
            const newStyle: PersistedStyle = {
              position: "fixed",
              top: event.clientY - dragOffset.y,
              left: event.clientX - dragOffset.x,
            }
            setStyle(newStyle);
            persistStyle(newStyle)
          }}
          onDragOver={(event) => {
            event.preventDefault();
            event.dataTransfer.dropEffect = "move";
          }}
        >
          <LogoIcon />
        </button>
      </div>

      {showControl && (
        <HypertuneToolbarControl
          {...styleProps}
          onClose={() => setShowControl(false)}
        />
      )}
    </>
  );
}

export function HypertuneToolbarControl({
  onClose,
  modalBackgroundClassName,
  modalClassName,
  headerClassName,
  mainClassName,
  flagClassName,
  flagNameContainerClassName,
  flagIconContainerClassName,
  flagNameClassName,
  flagValueContainerClassName,
  footerClassName,
  searchTextInputClassName,
  numberInputClassName,
  toggleClassName,
}: StyleProps & {
  onClose: () => void;
}): React.ReactElement | null {
  const hypertune = useHypertune();
  const setOverride = useSetOverride();

  const currentOverride = React.useMemo(
    () => (hypertune.props?.context?.override ?? {}) as ObjectValue,
    [hypertune.props?.context?.override]
  );

  const flagOverrides = React.useMemo(
    () =>
      Object.fromEntries(
        getFlagValues("", currentOverride).map(([flagPath, value]) => [
          flagPath.substring(rootPrefix.length),
          value,
        ])
      ) as Partial<FlagValues>,
    [currentOverride]
  );

  const setFlagOverride = React.useCallback(
    (flag: FlagPaths, value: Value | null) => {
      // We use JSON.parse(JSON.stringify()) to make a deep copy of the current override
      const newOverride: DeepPartial<Source> = JSON.parse(
        JSON.stringify(
          merge(
            currentOverride,
            getOverrideFromOverridesList([[`${rootPrefix}${flag}`, value]]) ??
              {}
          )
        )
      );

      if (!newOverride || Object.keys(newOverride).length === 0) {
        setOverride(null);
        return;
      }
      if (value === null) {
        const path = [rootFieldName, ...flag.split(".")];
        const parent = path
          .slice(0, -1)
          .reduce<ObjectValue>(
            (current, step) => current[step] as ObjectValue,
            newOverride as ObjectValue
          );
        delete parent[path[path.length - 1]];
      }
      console.debug("Set flag override", {
        flag,
        value,
        currentOverride,
        newOverride,
      });

      setOverride(newOverride);
    },
    [setOverride, currentOverride]
  );
  const clearOverrides = React.useCallback(() => {
    setOverride(null);
  }, [setOverride]);

  const flagPaths = React.useMemo(
    () => Object.keys(flagFallbacks) as FlagPaths[],
    []
  );
  const flagValues = React.useMemo(
    () =>
      hypertune.getFlagValues({
        flagFallbacks,
        flagPaths,
      }) as FlagValues,
    [flagPaths, hypertune]
  );

  console.debug("Flag overrides", {
    flagOverrides,
    override: hypertune.props?.context?.override,
    flagPaths,
  });

  const searchRef = React.useRef<HTMLInputElement>(null);
  React.useEffect(() => {
    searchRef.current?.focus();
  }, []);
  const [search, setSearch] = React.useState("");

  React.useEffect(() => {
    function down(event: KeyboardEvent): void {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
      }
    }

    document.addEventListener("keydown", down as any);
    return () => document.removeEventListener("keydown", down as any);
  }, [onClose]);

  return (
    <div
      className={`hypertuneToolbarModalBackground ${modalBackgroundClassName ?? ""}`}
      onClick={onClose}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          onClose();
        }
      }}
    >
      <div
        className={`hypertuneToolbarModal ${modalClassName ?? ""}`}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            onClose();
          }
        }}
      >
        <div className={`hypertuneToolbarHeader ${headerClassName ?? ""}`}>
          <MagnifyingGlassIcon style={{ marginRight: 11 }} />
          <input
            ref={searchRef}
            type="text"
            placeholder="Search for flags..."
            onChange={(event) => setSearch(event.target.value)}
            value={search}
            readOnly={false}
            className={`hypertuneToolbarSearchTextInput ${searchTextInputClassName ?? ""}`}
          />
          <Button
            text="Clear all overrides"
            disabled={Object.keys(flagOverrides).length === 0}
            onClick={clearOverrides}
          />
        </div>
        <div className={`hypertuneToolbarMainContainer ${mainClassName ?? ""}`}>
          {flagPaths
            .filter(
              (flag) =>
                (typeof flagValues[flag] === "boolean" ||
                  typeof flagValues[flag] === "number") &&
                (!search || flag.toLowerCase().includes(search.toLowerCase()))
            )
            .map((flag) => (
              <div 
                key={`toolbar-flag${flag}`}
                className={`hypertuneToolbarFlag ${flagClassName ?? ""}`}
              >
                <div className={`hypertuneToolbarFlagNameContainer ${flagNameContainerClassName ?? ""}`}>
                  <div className={`hypertuneToolbarFlagIconContainer ${flagIconContainerClassName ?? ""}`}>
                    <FlagIcon />
                  </div>
                  <p className={`hypertuneToolbarFlagName ${flagNameClassName ?? ""}`}>
                    {flag.split(".").map(toStartCase).join(" / ")}
                  </p>
                </div>

                <div className={`hypertuneToolbarFlagValueContainer ${flagValueContainerClassName ?? ""}`}>
                  <div style={{ width: "100px" }}>
                    {flagOverrides[flag] !== undefined && (
                      <Button
                        text="Clear override"
                        onClick={() => setFlagOverride(flag, null)}
                        style={{ padding: "5px" }}
                      />
                    )}
                  </div>

                  {typeof flagValues[flag] === "boolean" ? (
                    <Toggle
                      value={flagValues[flag] as any as boolean}
                      setValue={(newValue) => setFlagOverride(flag, newValue)}
                      className={toggleClassName}
                    />
                  ) : typeof flagValues[flag] === "number" ? (
                    <input
                      type="number"
                      readOnly={false}
                      value={flagValues[flag] as any as number}
                      onChange={(event) => {
                        if (event.target.value === "") {
                          return;
                        }
                        const newValue = parseFloat(event.target.value);
                        if (Number.isNaN(newValue)) {
                          return;
                        }
                        setFlagOverride(flag, newValue);
                      }}
                      className={`hypertuneToolbarNumberInput ${numberInputClassName ?? ""}`}
                    />
                  ) : null}
                </div>
              </div>
            ))}
        </div>
        <div className={`hypertuneToolbarFooter ${footerClassName ?? ""}`}>
          <LogoIcon />
        </div>
      </div>
    </div>
  );
}

function Button({
  text,
  onClick,
  disabled,
  style,
}: {
  text: string;
  onClick: () => void;
  disabled?: boolean;
  style?: React.CSSProperties;
}): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="hypertuneToolbarButton"
      style={style}
    >
      {text}
    </button>
  );
}

function Toggle({
  value,
  setValue,
  className,
}: {
  value: boolean;
  setValue?: (newValue: boolean) => void;
  className?: string;
}): React.ReactElement {
  return (
    <button
      type="button"
      role="switch"
      aria-label="Toggle"
      aria-checked={value}
      onClick={() => setValue?.(!value)}
      className={`hypertuneToolbarToggle ${className ?? ""}`}
    >
      <span />
    </button>
  );
}

function LogoIcon(): React.ReactElement {
  return (
    <svg
      height={20}
      viewBox="0 0 102 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect width="20" height="20" rx="6" fill="#22252b" />
      <rect
        x="4.58057"
        y="15.4192"
        width="15.7583"
        height="0.9375"
        rx="0.46875"
        transform="rotate(-45 4.58057 15.4192)"
        fill="white"
      />
      <rect
        x="11.0205"
        y="15.5327"
        width="6.71895"
        height="0.9375"
        rx="0.46875"
        transform="rotate(-45 11.0205 15.5327)"
        fill="white"
      />
      <rect
        x="4.375"
        y="6.875"
        width="2.5"
        height="2.5"
        rx="1.25"
        transform="rotate(-90 4.375 6.875)"
        fill="white"
      />
      <path
        d="M29.162 15V4.794H31.458V8.854H35.686V4.794H37.968V15H35.686V10.8H31.458V15H29.162ZM39.6749 17.338V15.658H42.3349V14.188L39.3669 7.482H41.6069L43.1189 11.528L43.3429 12.242H43.4269L43.6509 11.528L45.2049 7.482H47.4449L44.4769 14.188V15.966C44.4769 16.904 43.9029 17.338 43.0349 17.338H39.6749ZM48.6538 17.338V7.482H50.6978L50.8098 8.588H50.9078C51.2718 7.804 52.1258 7.342 53.1198 7.342C55.1358 7.342 56.0038 8.812 56.0038 11.094V11.416C56.0038 14.02 54.8838 15.14 53.1198 15.14C52.1398 15.14 51.2858 14.678 50.9078 13.894H50.8098V17.338H48.6538ZM52.2518 13.39C53.1758 13.39 53.7078 12.844 53.7078 11.892V10.604C53.7078 9.652 53.1758 9.092 52.2518 9.092C51.3698 9.092 50.8238 9.666 50.8238 10.632V11.864C50.8238 12.83 51.3698 13.39 52.2518 13.39ZM60.6925 15.14C58.1865 15.14 56.9685 13.586 56.9685 11.262V11.136C56.9685 8.7 58.4105 7.342 60.5245 7.342C62.8205 7.342 64.1225 8.742 64.1225 10.828V11.78H59.1525V12.172C59.1525 12.984 59.7545 13.502 60.6225 13.502C61.3925 13.502 61.9105 13.138 62.0085 12.578H64.0385C63.9545 14.104 62.5965 15.14 60.6925 15.14ZM59.1525 10.38V10.45H62.0085V10.38C62.0085 9.372 61.4345 8.938 60.5945 8.938C59.6985 8.938 59.1525 9.456 59.1525 10.38ZM72.9016 15C71.6976 15 71.2356 14.328 71.2356 13.334V5.13H73.3916V7.482H75.2536V9.218H73.3916V13.32H75.1556V15H72.9016ZM65.5796 15V9.148C65.5796 8.126 66.2516 7.482 67.2456 7.482H70.3116V9.218H67.7356V15H65.5796ZM79.1439 15.14C77.4779 15.14 76.6659 13.95 76.6659 12.158V7.482H78.8079V11.85C78.8079 13.04 79.3259 13.404 80.1239 13.404C81.0759 13.404 81.5659 12.718 81.5659 11.682V7.482H83.7079V15H81.7059L81.5519 13.768H81.4399C81.1039 14.594 80.2499 15.14 79.1439 15.14ZM85.5952 15V7.482H87.5972L87.7512 8.714H87.8772C88.1992 7.888 89.0252 7.342 90.1032 7.342C91.7132 7.342 92.7072 8.28 92.7072 10.394V15H90.5652V10.674C90.5652 9.47 90.0332 9.092 89.2212 9.092C88.2412 9.092 87.7512 9.778 87.7512 10.828V15H85.5952ZM97.8253 15.14C95.3193 15.14 94.1013 13.586 94.1013 11.262V11.136C94.1013 8.7 95.5433 7.342 97.6573 7.342C99.9533 7.342 101.255 8.742 101.255 10.828V11.78H96.2853V12.172C96.2853 12.984 96.8873 13.502 97.7553 13.502C98.5253 13.502 99.0433 13.138 99.1413 12.578H101.171C101.087 14.104 99.7293 15.14 97.8253 15.14ZM96.2853 10.38V10.45H99.1413V10.38C99.1413 9.372 98.5673 8.938 97.7273 8.938C96.8313 8.938 96.2853 9.456 96.2853 10.38Z"
        fill="#22252b"
      />
    </svg>
  );
}

function FlagIcon(): React.ReactElement {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      fill="white"
      viewBox="0 0 256 256"
    >
      <path d="M42.76,50A8,8,0,0,0,40,56V224a8,8,0,0,0,16,0V179.77c26.79-21.16,49.87-9.75,76.45,3.41,16.4,8.11,34.06,16.85,53,16.85,13.93,0,28.54-4.75,43.82-18a8,8,0,0,0,2.76-6V56A8,8,0,0,0,218.76,50c-28,24.23-51.72,12.49-79.21-1.12C111.07,34.76,78.78,18.79,42.76,50ZM216,172.25c-26.79,21.16-49.87,9.74-76.45-3.41-25-12.35-52.81-26.13-83.55-8.4V59.79c26.79-21.16,49.87-9.75,76.45,3.4,25,12.35,52.82,26.13,83.55,8.4Z" />
    </svg>
  );
}

function MagnifyingGlassIcon({
  style,
}: {
  style: React.CSSProperties;
}): React.ReactElement {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      fill="#7A8396"
      viewBox="0 0 256 256"
      style={style}
    >
      <path d="M229.66,218.34l-50.07-50.06a88.11,88.11,0,1,0-11.31,11.31l50.06,50.07a8,8,0,0,0,11.32-11.32ZM40,112a72,72,0,1,1,72,72A72.08,72.08,0,0,1,40,112Z" />
    </svg>
  );
}

function getFlagValues(
  keyPrefix: string,
  sourceObject: ObjectValue
): [string, Value][] {
  return Object.entries(sourceObject).flatMap(([flagKey, flagValue]) => {
    if (flagKey.startsWith("__") || Array.isArray(flagValue)) {
      return [];
    }

    if (typeof flagValue !== "object") {
      return [[`${keyPrefix}${flagKey}`, flagValue]];
    }
    return getFlagValues(`${keyPrefix}${flagKey}.`, flagValue);
  });
}
